import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import {
  HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX,
  HOME_LIST_TABLE_INACTIVE_TEXT_SX,
  HOME_LIST_TABLE_TOKENS,
} from "../../styles/homeListTableTokens";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";

const PREVIEW_CLAMP_SX = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
  fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
  fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
  lineHeight: "20px",
  color: HOME_LIST_TABLE_TOKENS.cellColor,
  maxWidth: "100%",
} as const;

const LINK_BUTTON_SX = {
  p: 0,
  minWidth: 0,
  textTransform: "none",
  fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
  flexShrink: 0,
} as const;

export type TaskTableLongTextCellProps = Readonly<{
  label: string;
  value: unknown;
  disabled: boolean;
  maxLen?: number;
  placeholder?: string;
  errorMessage?: string;
  onChange: (nextValue: string) => void;
}>;

function normalizeText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function resolveMaxLen(maxLen: number | undefined): number | undefined {
  return typeof maxLen === "number" && maxLen > 0 ? maxLen : undefined;
}

function LongTextPreview({
  label,
  text,
  disabled,
  onOpen,
}: Readonly<{
  label: string;
  text: string;
  disabled: boolean;
  onOpen: () => void;
}>) {
  if (!text.trim()) {
    if (disabled) {
      return (
        <Typography component="span" sx={HOME_LIST_TABLE_INACTIVE_TEXT_SX}>
          —
        </Typography>
      );
    }
    return (
      <Button
        variant="text"
        size="small"
        onClick={onOpen}
        sx={LINK_BUTTON_SX}
        aria-label={`Add ${label}`}
      >
        Add comment
      </Button>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.5,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography component="div" sx={PREVIEW_CLAMP_SX} title={text}>
        {text}
      </Typography>
      <Button
        variant="text"
        size="small"
        onClick={onOpen}
        sx={LINK_BUTTON_SX}
        aria-label={disabled ? `View ${label}` : `Edit ${label}`}
      >
        {disabled ? "View" : "Edit"}
      </Button>
    </Box>
  );
}

/**
 * Table cell for long comments (Assessment / Review / Reject Comments):
 * clamped preview in-cell; full view/edit in a dialog with optional maxLen.
 */
export function TaskTableLongTextCell({
  label,
  value,
  disabled,
  maxLen,
  placeholder,
  errorMessage,
  onChange,
}: TaskTableLongTextCellProps) {
  const text = normalizeText(value);
  const limit = resolveMaxLen(maxLen);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(text);

  useEffect(() => {
    if (open) {
      setDraft(text);
    }
  }, [open, text]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    const next =
      limit != null && draft.length > limit ? draft.slice(0, limit) : draft;
    onChange(next);
    setOpen(false);
  };

  const handleDraftChange = (next: string) => {
    if (limit != null && next.length > limit) {
      setDraft(next.slice(0, limit));
      return;
    }
    setDraft(next);
  };

  return (
    <>
      <Box
        sx={{
          ...HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX,
          alignItems: "flex-start",
          justifyContent: "center",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <LongTextPreview
          label={label}
          text={text}
          disabled={disabled}
          onOpen={() => setOpen(true)}
        />
        {errorMessage ? (
          <Typography
            component="span"
            role="alert"
            sx={{
              color: "error.main",
              fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
              fontSize: 12,
              lineHeight: "18px",
            }}
          >
            {errorMessage}
          </Typography>
        ) : null}
      </Box>

      <Dialog
        open={open}
        onClose={withoutBackdropClose(handleClose)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "12px" } } }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            fontSize: (theme) => theme.typography.subtitle1.fontSize,
          }}
        >
          {label}
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={DIALOG_CLOSE_BUTTON_SX}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: disabled ? 3 : 1 }}>
          {disabled ? (
            <Typography
              component="div"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
                fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
                lineHeight: "22px",
                color: HOME_LIST_TABLE_TOKENS.cellColor,
                minHeight: 120,
              }}
            >
              {text || "—"}
            </Typography>
          ) : (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={6}
              maxRows={16}
              placeholder={placeholder}
              value={draft}
              onChange={(event) => handleDraftChange(event.target.value)}
              slotProps={{
                htmlInput: limit != null ? { maxLength: limit } : undefined,
                formHelperText: { sx: { textAlign: "right", mx: 0 } },
              }}
              helperText={
                limit != null ? `${draft.length}/${limit}` : undefined
              }
            />
          )}
        </DialogContent>
        {disabled ? null : (
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ textTransform: "none" }}
            >
              Save
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

export default TaskTableLongTextCell;
